import { useState, useEffect } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LogisticsProviderForm, LogisticsProvider, RecommendationRule, formatRecommendationRule, provider_documents_label } from "@/components/logistics_provider_form"
import { api_fetch } from "@/api"
import { toast } from "@/components/ui/use-toast"
import { Plus, Edit, Trash2, ExternalLink } from "lucide-react"

interface LogisticsProvidersResponse {
  logistics_providers: LogisticsProvider[]
  guidance_url: string | null
}

export function LogisticsProvidersSettings() {
  const [providers, setProviders] = useState<LogisticsProvider[]>([])
  const [guidanceUrl, setGuidanceUrl] = useState<string>("")
  const [savedGuidanceUrl, setSavedGuidanceUrl] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [savingUrl, setSavingUrl] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingProvider, setEditingProvider] = useState<LogisticsProvider | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    loadProviders()
  }, [])

  async function loadProviders() {
    try {
      const response = await api_fetch<LogisticsProvidersResponse>("/organization/logistics-providers")
      
      if (response.ok) {
        setProviders(response.value.data.logistics_providers)
        const url = response.value.data.guidance_url || ""
        setGuidanceUrl(url)
        setSavedGuidanceUrl(url)
      } else {
        throw new Error(response.error?.message || "Failed to load logistics providers")
      }
    } catch (error) {
      console.error("Error loading logistics providers:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load logistics providers",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  async function handleSaveGuidanceUrl() {
    setSavingUrl(true)
    try {
      const response = await api_fetch("/organization/logistics-providers", {
        method: "PUT",
        body: { 
          logistics_providers: providers,
          guidance_url: guidanceUrl.trim() || null
        },
      })

      if (!response.ok) {
        throw new Error(response.error?.message || "Failed to save reference link")
      }

      setSavedGuidanceUrl(guidanceUrl.trim())
      toast({
        title: "Success",
        description: "Reference link saved successfully",
      })
    } catch (error) {
      console.error("Error saving reference link:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save reference link",
        variant: "destructive",
      })
    } finally {
      setSavingUrl(false)
    }
  }

  async function handleProvidersSaved(newProviders: LogisticsProvider[]) {
    // Reload providers from server to ensure we have the latest state
    await loadProviders()
    setShowForm(false)
    setEditingProvider(null)
    setEditingIndex(null)
  }

  function handleAddProvider() {
    setEditingProvider(null)
    setEditingIndex(null)
    setShowForm(true)
  }

  function handleEditProvider(provider: LogisticsProvider, index: number) {
    setEditingProvider(provider)
    setEditingIndex(index)
    setShowForm(true)
  }

  async function handleDeleteProvider(index: number) {
    const provider = providers[index]
    
    if (!confirm(`Are you sure you want to delete ${provider.name || provider.email}?`)) {
      return
    }

    try {
      const updatedProviders = providers.filter((_: LogisticsProvider, i: number) => i !== index)
      
      const response = await api_fetch("/organization/logistics-providers", {
        method: "PUT",
        body: { 
          logistics_providers: updatedProviders,
          guidance_url: savedGuidanceUrl || null
        },
      })

      if (!response.ok) {
        throw new Error(response.error?.message || "Failed to delete logistics provider")
      }

      toast({
        title: "Success",
        description: "Logistics provider deleted successfully",
      })

      // Reload providers from server to ensure we have the latest state
      await loadProviders()
    } catch (error) {
      console.error("Error deleting logistics provider:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete logistics provider",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <div class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div class="space-y-6">
      <div class="flex justify-between items-center">
        <div>
          <p class="text-sm text-gray-500 pt-3">
            Manage logistics providers that you can request BOL and POD from to help validate and dispute deductions.
          </p>
        </div>
      </div>

      <div class="border rounded-lg p-4 bg-gray-50">
        <Label htmlFor="guidance-url" class="font-medium">Reference Link (Optional)</Label>
        <p class="text-xs text-gray-500 mt-1 mb-2">
          Add a link to a Google Sheet or other document that helps determine which logistics provider to use.
        </p>
        <div class="flex gap-2 items-center">
          <Input
            id="guidance-url"
            type="url"
            value={guidanceUrl}
            onChange={(e) => setGuidanceUrl(e.currentTarget.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            class="flex-1"
          />
          {savedGuidanceUrl && (
            <a 
              href={savedGuidanceUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              class="text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-5 w-5" />
            </a>
          )}
          <Button
            variant="outline"
            onClick={handleSaveGuidanceUrl}
            disabled={savingUrl || guidanceUrl === savedGuidanceUrl}
          >
            {savingUrl ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {providers.length === 0 ? (
        <div class="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <h3 class="text-sm font-medium text-gray-900">No logistics providers</h3>
          <p class="mt-1 text-sm text-gray-500">
            Get started by adding your first logistics provider.
          </p>
          <div class="mt-6">
            <Button onClick={handleAddProvider}>
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </div>
      ) : (
        <div class="space-y-4">
          {providers.map((provider: LogisticsProvider, index: number) => (
            <div key={index} class="border rounded-lg p-4 bg-gray-50">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900">{provider.name}</h4>
                  <p class="text-sm text-gray-600">{provider.email}</p>
                  {provider.access_method && (
                    <p class="text-sm text-gray-500">
                      Access: <span class="font-medium">{provider.access_method === "portal" ? "Portal" : "Email"}</span>
                      {provider.access_method === "portal" && provider.portal_url && (
                        <span class="ml-2">({provider.portal_url})</span>
                      )}
                    </p>
                  )}
                  {provider.access_method !== "portal" && (
                    <p class="text-sm text-gray-500">Requests: {provider_documents_label(provider.documents)}</p>
                  )}
                  {provider.contact_name && (
                    <p class="text-sm text-gray-500">Contact: {provider.contact_name}</p>
                  )}
                  {provider.details && (
                    <p class="text-sm text-gray-500">Details / Order Types: {provider.details}</p>
                  )}
                  {provider.cc_emails && provider.cc_emails.length > 0 && (
                    <p class="text-sm text-gray-500">CC: {provider.cc_emails.join(", ")}</p>
                  )}
                  {provider.recommendation_rules && provider.recommendation_rules.length > 0 && (
                    <div class="mt-2 pt-2 border-t">
                      {provider.recommendation_rules.map((rule: RecommendationRule, ruleIndex: number) => (
                        <div key={ruleIndex} class="text-xs text-gray-600 mb-1 pl-2 italic">
                          {formatRecommendationRule(rule)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div class="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    // @ts-ignore
                    size="sm"
                    onClick={() => handleEditProvider(provider, index)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    // @ts-ignore
                    size="sm"
                    onClick={() => handleDeleteProvider(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          
          <div class="pt-4">
            <Button variant="outline" onClick={handleAddProvider}>
              <Plus className="h-4 w-4 mr-2" />
              Add Another Provider
            </Button>
          </div>
        </div>
      )}

      <LogisticsProviderForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false)
          setEditingProvider(null)
          setEditingIndex(null)
        }}
        onSave={handleProvidersSaved}
        existingProviders={providers}
        editingProvider={editingProvider}
        editingIndex={editingIndex}
        title={editingProvider ? "Edit Logistics Provider" : "Add Logistics Provider"}
        description={editingProvider 
          ? "Update the logistics provider information."
          : "Create a new logistics provider to send requests to."}
      />
    </div>
  )
}
